// src/pages/wizard/Step1ChooseConnector.jsx
import React, { useState } from 'react';
import {
  Search,
  Github,
  Trello,
  MessageSquare,
  Users,
  Briefcase,
  Receipt,
  Cloud,
  CreditCard,
  CheckSquare,
  GitPullRequest,
  TrendingUp,
  Clock,
  UtensilsCrossed,
  Dumbbell,
  UserPlus,
  Contact,
} from 'lucide-react';
import { Input } from '@/components/ui/input.jsx';

const availableConnectors = [
  // Active connectors (backend + Step2 auth wired)
  {
    id: 'github',
    name: 'GitHub',
    description: 'Repositories, Issues, Pull Requests, Commits, Users',
    icon: Github,
    iconColor: 'text-white',
    status: 'active',
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Issues, Projects, Sprints, Users, Workflows',
    icon: Trello,
    iconColor: 'text-blue-400',
    status: 'active',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Channels, Messages, Users, Files',
    icon: MessageSquare,
    iconColor: 'text-purple-400',
    status: 'active',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    description: 'Teams, Channels, Messages, Users, Meetings',
    icon: Users,
    iconColor: 'text-indigo-400',
    status: 'active',
  },
  {
    id: 'club_automation',
    name: 'Club Automation',
    description: 'Members, Check-ins, Classes, Billing, Attendance',
    icon: Dumbbell,
    iconColor: 'text-emerald-400',
    status: 'active',
  },
  {
    id: 'jazzhr',
    name: 'JazzHR',
    description: 'Jobs, Applicants, Applications, Hires, Activities',
    icon: UserPlus,
    iconColor: 'text-teal-400',
    status: 'active',
  },
  {
    id: 'namely',
    name: 'Namely',
    description: 'Employees, Org Hierarchy, Headcount Trends, HR KPIs',
    icon: Contact,
    iconColor: 'text-cyan-400',
    status: 'active',
  },

  // Coming soon
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Contacts, Companies, Deals, Tickets, Engagements',
    icon: Briefcase,
    iconColor: 'text-orange-400',
    status: 'coming_soon',
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Customers, Invoices, Payments, Expenses, Items',
    icon: Receipt,
    iconColor: 'text-green-400',
    status: 'coming_soon',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Accounts, Contacts, Opportunities, Leads, Cases',
    icon: Cloud,
    iconColor: 'text-sky-400',
    status: 'coming_soon',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Customers, Charges, Subscriptions, Invoices, Payouts',
    icon: CreditCard,
    iconColor: 'text-violet-400',
    status: 'coming_soon',
  },
  {
    id: 'asana',
    name: 'Asana',
    description: 'Projects, Tasks, Teams, Users',
    icon: CheckSquare,
    iconColor: 'text-rose-400',
    status: 'coming_soon',
  },
  {
    id: 'linear',
    name: 'Linear',
    description: 'Issues, Projects, Teams, Cycles',
    icon: GitPullRequest,
    iconColor: 'text-zinc-300',
    status: 'coming_soon',
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    description: 'Deals, Contacts, Activities, Pipelines',
    icon: TrendingUp,
    iconColor: 'text-lime-400',
    status: 'coming_soon',
  },
  {
    id: 'harvest',
    name: 'Harvest',
    description: 'Time Entries, Projects, Clients, Invoices',
    icon: Clock,
    iconColor: 'text-amber-400',
    status: 'coming_soon',
  },
  {
    id: 'toast',
    name: 'Toast',
    description: 'Locations, Products, Customers, Orders, Payments',
    icon: UtensilsCrossed,
    iconColor: 'text-red-400',
    status: 'coming_soon',
  },
];

export default function Step1ChooseConnector({ selected, onSelect }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConnectors = availableConnectors.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (connector) => {
    if (connector.status === 'coming_soon') return;
    onSelect(connector);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">
          Choose a Connector
        </h2>
        <p className="text-sm text-[#a0a0a0]">
          Select the data source you want to connect to Connex
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
        <Input
          type="text"
          placeholder="Search connectors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Connector Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredConnectors.map((connector) => {
          const Icon = connector.icon;
          const isSelected = selected?.id === connector.id;
          const isComingSoon = connector.status === 'coming_soon';

          return (
            <button
              key={connector.id}
              onClick={() => handleSelect(connector)}
              disabled={isComingSoon}
              aria-disabled={isComingSoon}
              className={`
                relative p-6 rounded-xl border-2 text-left transition-all
                ${isComingSoon
                  ? 'border-[#2a2a2a] bg-[#1a1a1a] opacity-50 cursor-not-allowed'
                  : isSelected
                    ? 'border-blue-500 bg-blue-500/10 cursor-pointer'
                    : 'border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#1a1a1a] cursor-pointer'
                }
              `}
            >
              {isComingSoon && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-[#2a2a2a] text-[#a0a0a0] border border-[#3a3a3a]">
                  Coming Soon
                </span>
              )}

              <div className="flex items-start gap-4">
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isSelected ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-[#0a0a0a] border border-[#2a2a2a]'}
                `}>
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-blue-400' : connector.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold mb-1 ${isSelected ? 'text-blue-400' : 'text-white'}`}>
                    {connector.name}
                  </h3>
                  <p className="text-sm text-[#a0a0a0]">
                    {connector.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
